import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiClient, useAuth } from "@/lib/auth";
import { useDemoMode } from "@/lib/demoMode";

const Ctx = createContext(null);
const EMPTY = { cards: {}, forms: [], fieldTypes: [], counters: {}, balance: null, activity: [], layout: { order: [], hidden: [] }, isOwner: false, ready: false };

// Every page an owner can link a card to.
export const SYSTEM_PAGES = [
  ["/admin", "Main board"], ["/admin/billing", "Credit Billing"], ["/admin/roi", "ROI board"],
  ["/admin/finance", "Finance overview"], ["/admin/finance/transactions", "Finance · Transactions"],
  ["/admin/finance/expenses", "Finance · Expenses"], ["/admin/finance/bills", "Finance · Bills"],
  ["/admin/finance/distributions", "Finance · Distributions"], ["/admin/finance/forecast", "Finance · Forecast"],
  ["/admin/finance/reports", "Finance · Reports"], ["/admin/contacts", "CRM Contacts"], ["/admin/briefs", "Engine Briefs"],
  ["/admin/documents", "Document Vault"], ["/admin/sms", "SMS console"], ["/admin/demos", "Client Demos"],
  ["/admin/pilot-requests", "Pilot Requests"], ["/admin/previews", "Micro-landings"], ["/admin/users", "Users & roles"],
  ["/admin/forms", "Form Builder"], ["/admin/sandbox", "Sandbox"], ["/admin/tutorials", "Tutorials"],
  ["/admin/nds", "Schema editor"], ["/admin/settings", "Settings"], ["/new-brief", "New brief (public)"],
];

export function OpsProvider({ children }) {
  const demo = useDemoMode();
  const { user } = useAuth();
  const [state, setState] = useState(EMPTY);

  const reload = useCallback(async () => {
    if (demo || !user) return;
    const [c, f, n, a, b, l] = await Promise.all([
      apiClient.get("/admin/ops/cards"), apiClient.get("/admin/ops/forms"), apiClient.get("/admin/ops/counters"),
      apiClient.get("/admin/ops/activity?limit=15"), apiClient.get("/admin/billing/summary").catch(() => ({ data: {} })),
      apiClient.get("/admin/ops/layout").catch(() => ({ data: { order: [], hidden: [] } })),
    ]).catch(() => []);
    if (!c) return;
    setState({
      cards: c.data.cards || {}, forms: f.data.forms || [], fieldTypes: f.data.field_types || [],
      counters: n.data.entries_week || {}, activity: a.data.items || [],
      balance: b.data?.current_balance_credits ?? null, layout: l.data, isOwner: user.role === "partner", ready: true,
    });
  }, [demo, user]);

  useEffect(() => { reload(); }, [reload]);
  const value = useMemo(() => ({
    ...state, reload, formById: (id) => state.forms.find((f) => f.id === id),
    saveLayout: async (layout) => {
      const { data } = await apiClient.put("/admin/ops/layout", layout);
      setState((s) => ({ ...s, layout: data }));
    },
  }), [state, reload]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useOps = () => useContext(Ctx) || { ...EMPTY, reload: () => {}, formById: () => null, saveLayout: async () => {} };
